/**
 * UI gating — spec §I2 ("mirror of server rules, never the enforcement").
 * Nav: Data Manager analyst+ · Administration manager. Both the topbar nav filter and
 * the router guard read these two constants, so they can never drift apart.
 *
 * `openAccess` is the newer, coarser gate over all of it — see its own note.
 */
import type { MetaConfig } from './api';
import type { Role } from './state';

/**
 * Is the §I1 **read** surface open to everyone? (`features.open_access`, server-owned.)
 *
 * Client directive, 2026-09-08: *"we are not implementing user permissions at this stage —
 * take the user permissions off so everyone can see everything now. I am not supposed to be
 * viewing guest."* The server's `OPEN_ACCESS` switch is what actually opens the data
 * (`apps/accounts/serializers.py`, `current_role`); this reads the mirror of it out of
 * `/meta/config` so the UI stops printing locks over data nothing withholds — the Indicators
 * icon, the permalink gate, the persona label in the topbar and on the status bar.
 *
 * **It does not touch the two constants above.** `/auth/me` still reports the caller's real
 * role while the switch is on, precisely so Data Manager and Administration stay gated:
 * see everything means read everything, not administer anonymously.
 *
 * Defaults to **false** on an unanswered config: gated-until-told is a portal that briefly
 * asks for less, not one that briefly shows more. Permissions return as a later client task,
 * and turning the server's switch off is all it takes to bring these states back.
 */
export function openAccess(config: MetaConfig | undefined): boolean {
  return config?.features?.open_access === true;
}

/** Roles, lowest privilege first (§I1). */
export const ROLE_ORDER: Role[] = ['public', 'field', 'partner', 'analyst', 'manager'];

/** Route → roles allowed to open it. Absent = every role. */
export const ROUTE_ROLES: Partial<Record<string, Role[]>> = {
  '/manager': ['analyst', 'manager'],
  '/admin': ['manager'],
};

export function canAccess(path: string, role: Role): boolean {
  const allowed = ROUTE_ROLES[path];
  return !allowed || allowed.includes(role);
}

/** `atLeast('analyst', role)` — ordering per §I1, used for disabled-with-tooltip states. */
export function atLeast(minimum: Role, role: Role): boolean {
  return ROLE_ORDER.indexOf(role) >= ROLE_ORDER.indexOf(minimum);
}
